/* eslint-disable no-unused-expressions */

import React, { Component } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { withTranslation } from "react-i18next";
import { post } from "../../../utils/sysAxios";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { confirmAlert } from "react-confirm-alert";
import { toastError } from "../../../utils/sysToast";

/**
 * Bulk electricity recharge (Postpaid).
 * Recharges each unit's meter and raises an unpaid bill for the same amount.
 * Stages: setup (template + file) -> preview -> results.
 */
class BulkElectricityRecharge extends Component {
  state = {
    stage: "setup", // "setup" | "preview" | "results"
    compoundId: "",
    dueDate: "",
    file: null,
    previewRows: [],
    applyResults: [],
    compoundsDropdown: [],
  };

  componentDidMount() {
    this.getCompoundsDropdown();
  }

  getCompoundsDropdown() {
    post(
      "/api/data/GetAllCompoundsDropdown",
      null,
      this.props.updateIsLoading
    ).then((apiResponse) => {
      this.setState({ compoundsDropdown: apiResponse.data.respData.data });
    });
  }

  resetState() {
    this.setState({
      stage: "setup",
      compoundId: "",
      dueDate: "",
      file: null,
      previewRows: [],
      applyResults: [],
    });
  }

  close() {
    this.resetState();
    this.props.onClose();
  }

  downloadTemplate() {
    this.props.updateIsLoading(true);
    axios
      .post(
        "/api/data/DownloadBulkElectricityTemplate",
        { compoundId: this.state.compoundId || 0 },
        {
          headers: { Authorization: "Bearer " + sessionStorage.getItem("748784") },
          responseType: "blob",
        }
      )
      .then((res) => {
        saveAs(res.data, "BulkElectricityTemplate.xlsx");
      })
      .finally(() => {
        this.props.updateIsLoading(false);
      });
  }

  doPreview() {
    const { file } = this.state;
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file, file.name);

    post(
      "/api/data/PreviewBulkElectricityRecharge",
      formData,
      this.props.updateIsLoading
    ).then((apiResponse) => {
      if (apiResponse && apiResponse.data.respCode === 0) {
        this.setState({
          previewRows: apiResponse.data.respData.rows,
          stage: "preview",
        });
      }
    });
  }

  doApply() {
    if (!this.state.dueDate) {
      toastError(this.props.t("bulkElec.dueDateRequired"));
      return;
    }

    const validRows = this.state.previewRows.filter((r) => r.status === "Valid");

    post(
      "/api/data/ApplyBulkElectricityRecharge",
      { dueDate: this.state.dueDate, rows: validRows },
      this.props.updateIsLoading
    ).then((apiResponse) => {
      if (apiResponse && apiResponse.data.respCode === 0) {
        this.setState({
          applyResults: apiResponse.data.respData.results,
          stage: "results",
        });
        this.props.onCompleted && this.props.onCompleted();
      }
    });
  }

  validRows() {
    return this.state.previewRows.filter((r) => r.status === "Valid");
  }

  validTotal() {
    return this.validRows().reduce((sum, r) => sum + (r.amount || 0), 0);
  }

  confirmApply() {
    const { t } = this.props;
    const count = this.validRows().length;
    const total = this.validTotal().toLocaleString();

    confirmAlert({
      title: t("btn.bulkChargeElectricity"),
      message: `${t("bulkElec.confirmMessage")} (${count} ${t(
        "bulkElec.unitsWillBeCharged"
      )}, ${t("col.amount")}: ${total})`,
      buttons: [
        { label: t("btn.yes"), onClick: () => this.doApply() },
        { label: t("btn.cancel"), onClick: () => null },
      ],
    });
  }

  render() {
    const { t } = this.props;
    const { stage } = this.state;

    return (
      <Dialog
        visible={this.props.visible}
        style={{ width: stage === "setup" ? "55vw" : "80vw" }}
        onHide={() => this.close()}
        dismissableMask={false}
        closable={false}
        header={<h5 className="modal-header">{t("btn.bulkChargeElectricity")}</h5>}
        footer={
          <div>
            {stage === "setup" && (
              <Button
                label={t("bulk.preview")}
                icon="fa fa-search"
                className="p-button-rounded p-button-secondary"
                disabled={!this.state.file || this.props.isLoading}
                onClick={() => this.doPreview()}
              />
            )}
            {stage === "preview" && (
              <>
                <Button
                  label={t("bulk.confirmApply")}
                  icon="fa fa-bolt"
                  className="p-button-rounded p-button-success"
                  disabled={this.validRows().length === 0 || this.props.isLoading}
                  onClick={() => this.confirmApply()}
                />
                <Button
                  label={t("bulk.back")}
                  icon="fa fa-arrow-left"
                  className="p-button-rounded p-button-secondary"
                  style={{ marginLeft: "8px" }}
                  disabled={this.props.isLoading}
                  onClick={() => this.setState({ stage: "setup" })}
                />
              </>
            )}
            <Button
              label={t("btn.close")}
              icon="fa fa-close"
              className="p-button-rounded p-button-danger"
              style={{ marginLeft: "8px" }}
              onClick={() => this.close()}
            />
          </div>
        }
      >
        {/* Stage 1: Setup */}
        {stage === "setup" && (
          <div>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
                {t("col.compound")}:
              </label>
              <Dropdown
                value={this.state.compoundId}
                options={this.state.compoundsDropdown}
                showClear
                filter
                onChange={(e) => this.setState({ compoundId: e.value || "" })}
                placeholder={t("bulk.allCompounds")}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <Button
                label={t("btn.downloadTemplate")}
                icon="fa fa-download"
                className="p-button-rounded p-button-info"
                disabled={this.props.isLoading}
                onClick={() => this.downloadTemplate()}
              />
              <span style={{ marginLeft: "10px", color: "#666", fontSize: "12px" }}>
                {t("bulkElec.downloadHint")}
              </span>
            </div>
            <hr />
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
                {t("col.dueDate")}
                <span style={{ color: "#DC3545" }}> *</span>:
              </label>
              <Calendar
                showIcon
                value={this.state.dueDate}
                onChange={(e) => this.setState({ dueDate: e.value })}
                placeholder={t("col.dueDate")}
              />
              <span style={{ marginLeft: "10px", color: "#666", fontSize: "12px" }}>
                {t("bulkElec.dueDateHint")}
              </span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "black" }}>
                {t("btn.excelFile")}:
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "block" }}
                onChange={(e) => this.setState({ file: e.target.files[0] || null })}
              />
            </div>
            <p style={{ marginTop: "10px", color: "#666", fontSize: "12px" }}>
              {t("bulkElec.uploadHint")}
            </p>
          </div>
        )}

        {/* Stage 2: Preview */}
        {stage === "preview" && (
          <div>
            <div style={{ marginBottom: "12px", display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#d4edda", color: "#155724", padding: "4px 10px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                <b>{this.validRows().length}</b>
                {t("bulkElec.toRecharge")}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f8d7da", color: "#721c24", padding: "4px 10px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                <b>{this.state.previewRows.filter((r) => r.status === "Error").length}</b>
                {t("bulk.errors")}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#e2e3e5", color: "#383d41", padding: "4px 10px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                {t("bulkElec.totalAmount")}: <b>{this.validTotal().toLocaleString()}</b>
              </span>
            </div>
            <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <table className="table table-sm table-bordered" style={{ fontSize: "13px" }}>
                <thead className="thead-light" style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ width: "55px" }}>{t("btn.row")}</th>
                    <th>{t("col.unitName")}</th>
                    <th>{t("col.compound")}</th>
                    <th>{t("col.ownerName")}</th>
                    <th style={{ width: "110px" }}>{t("col.amount")}</th>
                    <th style={{ width: "80px" }}>{t("col.status")}</th>
                    <th>{t("col.details")}</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.previewRows.map((r, i) => (
                    <tr key={i} className={r.status === "Error" ? "table-danger" : "table-success"}>
                      <td>{r.row}</td>
                      <td>{r.unitName}</td>
                      <td>{r.compoundName}</td>
                      <td>{r.ownerName}</td>
                      <td>{(r.amount || 0).toLocaleString()}</td>
                      <td>{r.status}</td>
                      <td>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stage 3: Results */}
        {stage === "results" && (
          <div>
            <p style={{ marginBottom: "8px", fontWeight: "bold", color: "black" }}>
              {this.state.applyResults.filter((r) => r.status === "Success").length}{" "}
              {t("btn.of")} {this.state.applyResults.length}{" "}
              {t("bulkElec.rowsRecharged")}
            </p>
            <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <table className="table table-sm table-bordered" style={{ fontSize: "13px" }}>
                <thead className="thead-light" style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ width: "55px" }}>{t("btn.row")}</th>
                    <th>{t("col.unitName")}</th>
                    <th style={{ width: "110px" }}>{t("col.amount")}</th>
                    <th style={{ width: "80px" }}>{t("col.status")}</th>
                    <th>{t("col.details")}</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.applyResults.map((r, i) => (
                    <tr key={i} className={r.status === "Success" ? "table-success" : "table-danger"}>
                      <td>{r.row}</td>
                      <td>{r.unitName}</td>
                      <td>{(r.amount || 0).toLocaleString()}</td>
                      <td>{r.status}</td>
                      <td>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Dialog>
    );
  }
}

export default withTranslation()(BulkElectricityRecharge);
